import { Box, Typography, Paper } from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { ReactComponent as InfoIcon } from '../../assets/img/info-icon.svg';
import './BirdHeightDistributionV3.css';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorState } from '../common/ErrorState';
import { NoDataState } from '../common/NoDataState';

let subDescription: string =
  'This graph shows how much bird activity was recorded at different heights. The activity is measured in bird-seconds per day, meaning how long birds were present on an average day within a box of air that is 100 meters wide, 100 meters long, and 5 meters high.';

export const BirdHeightDistributionV3 = (props: any) => {
  const { isLoading, isError, error, hasData, data, onRetry } = props;

  // Show loading state
  if (isLoading) {
    return (
      <Paper
        className="custom-chart"
        sx={{
          minWidth: '1000px',
          maxWidth: '100%',
          minHeight: '800px',
          marginBottom: '7px',
          padding: '32px',
          borderRadius: '16px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(229, 231, 235, 0.8)',
          background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
          overflow: 'hidden',
        }}
      >
        <LoadingSpinner
          message="Loading bird height distribution data..."
          height="800px"
          showBackground={false}
        />
      </Paper>
    );
  }

  // Show error state
  if (isError) {
    return (
      <Paper
        className="custom-chart"
        sx={{
          minWidth: '1000px',
          maxWidth: '100%',
          minHeight: '800px',
          marginBottom: '7px',
          padding: '32px',
          borderRadius: '16px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(229, 231, 235, 0.8)',
          background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
          overflow: 'hidden',
        }}
      >
        <ErrorState
          title="Failed to load bird height distribution data"
          error={error}
          height="800px"
          showBackground={false}
          onRetry={onRetry}
        />
      </Paper>
    );
  }

  // Show no data state
  if (!hasData) {
    return (
      <Paper
        className="custom-chart"
        sx={{
          minWidth: '1000px',
          maxWidth: '100%',
          minHeight: '800px',
          marginBottom: '7px',
          padding: '32px',
          borderRadius: '16px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(229, 231, 235, 0.8)',
          background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
          overflow: 'hidden',
        }}
      >
        <NoDataState
          title="No bird height distribution data available"
          message="No bird height data found for the selected date range and filters. Try adjusting your selection criteria."
          height="800px"
          showBackground={false}
        />
      </Paper>
    );
  }

  if (!data || !data[0] || !data[0].data) {
    return null;
  }

  const rawData = data[0].data;
  if (!rawData || rawData.length === 0) {
    return null;
  }

  const sortedData = [...rawData].sort((a: any, b: any) => a.height - b.height);
  
  const maxHeight = sortedData.length > 0 ? Math.max(...sortedData.map((d: any) => d.height)) : 0;
  
  const valueMap = new Map<number, number>();
  sortedData.forEach((d: any) => {
    const height = d.height;
    const value = d.avg_bird_seconds_per_50000_cubic_meter != null && !isNaN(Number(d.avg_bird_seconds_per_50000_cubic_meter))
      ? Number(d.avg_bird_seconds_per_50000_cubic_meter)
      : 0;
    valueMap.set(height, value);
  });
  
  const heightBins: string[] = [];
  const heightLabels: string[] = [];
  const values: number[] = [];
  
  for (let h = 0; h <= maxHeight; h += 5) {
    const binStart = h;
    const binEnd = binStart + 5;
    const binKey = `${binStart}-${binEnd}m`;
    const label = `${binStart}m`;
    
    heightBins.push(binKey);
    heightLabels.push(label);
    values.push(valueMap.get(h) ?? 0);
  }
  
  const heightBinsReversed = [...heightBins].reverse();
  const heightLabelsReversed = [...heightLabels].reverse();
  const valuesReversed = [...values].reverse();
  
  const total = heightLabelsReversed.length;
  const spacing = total <= 25 ? 5 : total <= 50 ? 10 : total <= 100 ? 25 : 50;
  const filteredLabels = heightLabelsReversed.map((label, idx) => {
    const height = parseInt(label.replace('m', ''));
    if (idx === 0 || idx === total - 1 || height % spacing === 0) return label;
    return '';
  });
  
  const maxValue = Math.max(...valuesReversed, 0);

  let xAxisMax: number | undefined;
  let xAxisTickAmount: number | undefined;

  if (maxValue <= 0) {
    xAxisMax = undefined;
    xAxisTickAmount = 4;
  } else if (maxValue <= 10) {
    xAxisMax = 10;
    xAxisTickAmount = 5;
  } else if (maxValue <= 20) {
    xAxisMax = 20;
    xAxisTickAmount = 4;
  } else if (maxValue <= 50) {
    xAxisMax = 50;
    xAxisTickAmount = 5;
  } else if (maxValue <= 100) {
    xAxisMax = 100;
    xAxisTickAmount = 5;
  } else if (maxValue <= 250) {
    xAxisMax = 250;
    xAxisTickAmount = 5;
  } else if (maxValue <= 500) {
    xAxisMax = 500;
    xAxisTickAmount = 5;
  } else if (maxValue <= 1000) {
    xAxisMax = 1000;
    xAxisTickAmount = 5;
  } else if (maxValue <= 2000) {
    xAxisMax = 2000;
    xAxisTickAmount = 5;
  } else if (maxValue <= 3000) {
    xAxisMax = 3000;
    xAxisTickAmount = 5;
  } else if (maxValue <= 4000) {
    xAxisMax = 4000;
    xAxisTickAmount = 5;
  } else {
    xAxisMax = Math.ceil(maxValue / 1000) * 1000;
    xAxisTickAmount = 5;
  }
  
  const tooltipFormatter = ({ dataPointIndex }: any) => {
    const heightBin = heightBinsReversed[dataPointIndex];
    const value = valuesReversed[dataPointIndex];
    return `
      <div style="padding: 8px;">
        <div style="font-weight: 600; margin-bottom: 4px;">Height: ${heightBin}</div>
        <div>Average bird seconds activity: <strong>${value.toFixed(2)}</strong></div>
      </div>
    `;
  };

  const series = [{ 
    name: 'avg_bird_seconds_activity_per_50000_cubic_meter',
    data: valuesReversed 
  }];

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      stacked: false,
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '80%',
        borderRadius: 0,
      },
    },
    colors: ['#6A5ACD'],
    dataLabels: {
      enabled: false,
    },
    legend: { 
      show: false 
    },
    grid: {
      show: false,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: false } },
    },
    xaxis: {
      categories: filteredLabels,
      title: {
        text: 'Average bird-seconds activity per day (s)',
        style: {
          fontSize: '14px',
          color: '#8B8296',
          fontFamily: 'inherit',
        },
      },
      labels: {
        style: { 
          fontSize: '12px', 
          colors: '#8B8296',
          fontFamily: 'inherit',
        },
      },
    },
    yaxis: {
      title: {
        text: 'Height bin (m)',
        style: {
          fontSize: '14px',
          color: '#8B8296',
          fontFamily: 'inherit',
        },
      },
      labels: {
        style: {
          fontSize: '12px',
          colors: '#8B8296',
          fontFamily: 'inherit',
        },
        offsetY: 3,
      },
      min: 0,
      max: xAxisMax,
      tickAmount: xAxisTickAmount,
    },
    tooltip: {
      enabled: true,
      custom: tooltipFormatter,
      theme: 'light',
      style: {
        fontSize: '12px',
        fontFamily: 'inherit',
      },
    },
  };

  return (
    <Paper
      className="custom-chart"
      sx={{
        minWidth: '1000px',
        maxWidth: '100%',
        minHeight: '800px',
        marginBottom: '7px',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(229, 231, 235, 0.8)',
        background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '23px',
          padding: '20px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '11px',
            alignItems: 'center',
            paddingTop: '7px',
          }}
        >
          <InfoIcon />
          <Box
            sx={{
              borderLeft: '2px solid #45009C',
              height: '50px',
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography sx={{ color: '#17052D' }} variant="h6">
            Distribution of bird flight heights
          </Typography>
          <Typography sx={{ color: '#8B8296', fontSize: '14px' }} variant="body2">
            {subDescription}
          </Typography>
          <Typography sx={{ color: '#8B8296', fontSize: '12px', fontStyle: 'italic', marginTop: '8px' }} variant="body2">
            Note: This graph does not filter by species.
          </Typography>
        </Box>
      </Box>
      <Box sx={{ width: '100%', padding: '20px 20px 20px 40px' }}>
        <ReactApexChart
          options={options}
          series={series}
          type="bar"
          height={heightBinsReversed.length > 0 ? Math.max(400, Math.min(800, heightBinsReversed.length * 15)) : 400}
          width="100%"
        />
      </Box>
    </Paper>
  );
}; 